// React-query hooks over the cart + checkout API. All mutations
// invalidate the cart query so cart-derived UI (badge, quote, sections)
// re-fetches automatically.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteCart,
  finalizeCheckout,
  getCart,
  getCartQuote,
  itemsApi,
  patchCart,
  patchCartInsurance
} from "./api";
import type {
  Cart,
  CartItem,
  CartPayload,
  CartQuoteResponse,
  FinalizeRequest,
  FinalizeResponse
} from "./types";

export const CART_QUERY_KEY = ["cart"] as const;
export const CART_QUOTE_QUERY_KEY = ["cart", "quote"] as const;

export function useCart() {
  return useQuery<Cart>({
    queryKey: CART_QUERY_KEY,
    queryFn: getCart,
    // The cart is mutated only via this app's own UI, so cached data is
    // safe for ~minutes. We still invalidate after every mutation.
    staleTime: 60_000
  });
}

// Quote runs server-side on every call (re-quote on every change is
// the FR-12 contract — the surcharge has to be visible BEFORE the CTA).
// Cache for 5s so rapid re-renders (e.g. accordion toggles) don't spam.
//
// Not consumed by CartPage in this slice (the cart sidebar punts the
// price breakdown to checkout). The checkout slice landing next will
// wire it into the section state machine. Kept here so all cart
// hooks live together and to lock the contract that the cart spec
// already exercises.
export function useCartQuote(enabled = true) {
  return useQuery<CartQuoteResponse>({
    queryKey: CART_QUOTE_QUERY_KEY,
    queryFn: getCartQuote,
    enabled,
    staleTime: 5_000,
    // Distinguish "no items yet" 422 from network failure — return the
    // error body as data so the UI can render field-level errors.
    retry: false
  });
}

// Write the freshly-returned cart into the cache directly so a
// subsequent `useCart()` render reads the new value without a
// network round-trip. The quote query is invalidated (not seeded)
// because the server has to recompute totals against the new items.
function applyCartMutation(qc: ReturnType<typeof useQueryClient>, cart: Cart): Promise<unknown> {
  qc.setQueryData(CART_QUERY_KEY, cart);
  return qc.invalidateQueries({ queryKey: CART_QUOTE_QUERY_KEY });
}

function invalidateCart(qc: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: CART_QUERY_KEY }),
    qc.invalidateQueries({ queryKey: CART_QUOTE_QUERY_KEY })
  ]);
}

export function usePatchCart() {
  const qc = useQueryClient();
  return useMutation<Cart, Error, Partial<CartPayload>>({
    mutationFn: patchCart,
    onSuccess: (cart) => applyCartMutation(qc, cart)
  });
}

export function useDeleteCart() {
  const qc = useQueryClient();
  return useMutation<null, Error, void>({
    mutationFn: deleteCart,
    onSuccess: () => invalidateCart(qc)
  });
}

export function useAddItem() {
  const qc = useQueryClient();
  return useMutation<Cart, Error, CartItem>({
    mutationFn: itemsApi.add,
    onSuccess: (cart) => applyCartMutation(qc, cart)
  });
}

export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation<Cart, Error, { index: number; patch: Partial<CartItem> }>({
    mutationFn: ({ index, patch }) => itemsApi.update(index, patch),
    onSuccess: (cart) => applyCartMutation(qc, cart)
  });
}

// Phase 4c COI waiver bypass — cart-level chooser. The server response
// is `{ ok, cart: { id, damage_waiver_opted_in } }`; we fold the new
// flag into the cached cart so the chooser visually settles before the
// next `useCart()` refetch lands.
export function useSetCartWaiver() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; cart: { id: number; damage_waiver_opted_in: boolean } }, Error, boolean>({
    mutationFn: patchCartInsurance,
    onSuccess: ({ cart }) => {
      qc.setQueryData<Cart | undefined>(CART_QUERY_KEY, (prev) =>
        prev ? { ...prev, damage_waiver_opted_in: cart.damage_waiver_opted_in } : prev
      );
      return qc.invalidateQueries({ queryKey: CART_QUOTE_QUERY_KEY });
    }
  });
}

export function useRemoveItem() {
  const qc = useQueryClient();
  return useMutation<Cart, Error, number>({
    mutationFn: itemsApi.remove,
    onSuccess: (cart) => applyCartMutation(qc, cart)
  });
}

// The finalize hook owns the Idempotency-Key. The caller passes the
// key explicitly on each invocation so a retry of the same checkout
// attempt deduplicates server-side.
export function useFinalizeCheckout() {
  const qc = useQueryClient();
  return useMutation<
    FinalizeResponse,
    Error,
    { idempotencyKey: string; body: FinalizeRequest }
  >({
    mutationFn: ({ idempotencyKey, body }) => finalizeCheckout(idempotencyKey, body),
    onSuccess: () => invalidateCart(qc) // cart gets cleared by the success path
  });
}
